import React, { useEffect, useMemo, useState } from "react";
import { getTrainingDays, getTrainingDetails, duplicateTrainingMultiple } from "../../services/authService";

// Retourne une date locale à 00:00 pour éviter les dérives UTC.
function atMidnight(date) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
}

function addDays(date, n) {
	const d = new Date(date);
	d.setDate(d.getDate() + n);
	return d;
}

function fmtInput(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

// Récupère toutes les séances (non-templates) entre `from` et `to` inclus pour
// un élève, en interrogeant les mois concernés.
// Optimisation : on cache par (year, month) pour ne pas rappeler
// getTrainingDays plusieurs fois pour le même mois.
async function fetchSessionsInRange(studentId, from, to) {
	const start = atMidnight(from);
	const end = atMidnight(to);
	const monthCache = new Map(); // "YYYY-M" -> [day numbers]
	const results = [];
	const cursor = new Date(start);
	while (cursor.getTime() <= end.getTime()) {
		const y = cursor.getFullYear();
		const m = cursor.getMonth() + 1;
		const key = `${y}-${m}`;
		if (!monthCache.has(key)) {
			const res = await getTrainingDays(studentId, y, m);
			monthCache.set(key, res.success ? (res.trainingDays || []) : []);
		}
		const dayNum = cursor.getDate();
		if (monthCache.get(key).includes(dayNum)) {
			const details = await getTrainingDetails(studentId, y, m, dayNum);
			if (details.success && details.trainings) {
				details.trainings.forEach((t) => {
					if (t.estTemplate || t.EstTemplate) return;
					results.push({
						id: t.id,
						nom: t.nom || "Séance",
						date: atMidnight(cursor).getTime(),
						nbExos: (t.exercices || []).filter((ex) => ex.supprime !== 1).length,
					});
				});
			}
		}
		cursor.setDate(cursor.getDate() + 1);
	}
	// Plus récent en premier — le coach cherche presque toujours dans le récent.
	results.sort((a, b) => b.date - a.date);
	return results;
}

export default function DuplicateSessionModal({ student, initialTargetDate, onClose, onDone, onNotif }) {
	const today = useMemo(() => atMidnight(new Date()), []);
	const [rangeDays, setRangeDays] = useState(30);
	const [targetDate, setTargetDate] = useState(atMidnight(initialTargetDate || new Date()));
	const [loading, setLoading] = useState(false);
	const [sessions, setSessions] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");

	const rangePresets = [
		{ label: "30 jours", value: 30 },
		{ label: "3 mois", value: 90 },
		{ label: "6 mois", value: 180 },
	];

	// Charge les séances de la plage à chaque changement de rangeDays.
	// La sélection est réinitialisée : si la sélection précédente sort de la
	// nouvelle plage, la garder afficherait "aucune séance mais un id sélectionné".
	useEffect(() => {
		if (!student?.id) return;
		let cancelled = false;
		setLoading(true);
		setError("");
		setSelectedId(null);
		const from = addDays(today, -rangeDays);
		const to = today;
		fetchSessionsInRange(student.id, from, to).then((list) => {
			if (cancelled) return;
			setSessions(list);
			setLoading(false);
		}).catch(() => {
			if (cancelled) return;
			setError("Erreur lors du chargement des séances");
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, [student?.id, rangeDays, today]);

	const selectedSession = sessions.find((s) => s.id === selectedId) || null;

	const handleConfirm = async () => {
		if (!student?.id || !selectedSession) return;
		setSubmitting(true);
		setError("");
		// resetPerformance:true = on garde structure + charges, on vide reps/rpe/commentaire.
		// C'est le comportement natif de l'app mobile pour la duplication d'une séance.
		const res = await duplicateTrainingMultiple(student.id, selectedSession.id, [targetDate.getTime()], true);
		setSubmitting(false);
		if (res.success) {
			onNotif?.("Séance dupliquée");
			onDone?.();
			onClose?.();
		} else {
			setError(res.error || "Erreur lors de la duplication");
		}
	};

	// Groupement visuel par mois pour aérer la liste (Nov 2025, Oct 2025…).
	// Les sessions sont déjà triées desc, donc on peut découper séquentiellement.
	const groupedSessions = useMemo(() => {
		const groups = [];
		let currentKey = null;
		sessions.forEach((s) => {
			const d = new Date(s.date);
			const key = `${d.getFullYear()}-${d.getMonth()}`;
			if (key !== currentKey) {
				currentKey = key;
				groups.push({
					key,
					label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
					items: [],
				});
			}
			groups[groups.length - 1].items.push(s);
		});
		return groups;
	}, [sessions]);

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			{/* Réutilise le style .dup-week-modal : taille fixe, header + zone
			    contrôles fixe, liste scrollable, footer fixe. Cohérence visuelle
			    avec "Dupliquer une semaine" qui vit à côté. */}
			<div className="mens-modal dup-week-modal" onClick={(e) => e.stopPropagation()}>
				<div className="mens-modal-header dup-week-modal-header">
					<h3>Dupliquer une séance · {student?.prenom || "Élève"}</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				<div className="dup-week-modal-controls">
					<div>
						<label className="mens-modal-label" style={{ display: "block", marginBottom: 6 }}>
							Date cible
						</label>
						<input
							type="date"
							value={fmtInput(targetDate)}
							onChange={(e) => {
								if (!e.target.value) return;
								setTargetDate(atMidnight(new Date(e.target.value + "T00:00:00")));
							}}
							className="mens-modal-input"
						/>
					</div>

					<div style={{ marginTop: 12 }}>
						<label className="mens-modal-label" style={{ display: "block", marginBottom: 6 }}>
							Séance à dupliquer · chercher dans
						</label>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
							{rangePresets.map((p) => (
								<button
									key={p.value}
									type="button"
									className={`editor-panel-templates-folderpill ${rangeDays === p.value ? "active" : ""}`}
									onClick={() => setRangeDays(p.value)}
								>
									{p.label}
								</button>
							))}
						</div>
					</div>
				</div>

				<div className="dup-week-modal-scroll">
					<div style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
						{loading && (
							<div className="dup-week-empty">Chargement des séances…</div>
						)}
						{!loading && sessions.length === 0 && (
							<div className="dup-week-empty">
								Aucune séance sur cette période. Élargis la plage ci-dessus.
							</div>
						)}
						{!loading && sessions.length > 0 && (
							<div className="dup-session-groups">
								{groupedSessions.map((g) => (
									<div key={g.key} className="dup-session-group">
										<div className="dup-session-group-label">{g.label}</div>
										<div className="dup-session-list">
											{g.items.map((s) => {
												const checked = s.id === selectedId;
												const d = new Date(s.date);
												return (
													<label
														key={s.id}
														className={`dup-week-item ${checked ? "checked" : ""}`}
													>
														<input
															type="radio"
															name="dup-session-pick"
															checked={checked}
															onChange={() => setSelectedId(s.id)}
															disabled={submitting}
															className="dup-week-item-check"
														/>
														<div className="dup-week-item-info">
															<span className="dup-week-item-name">{s.nom}</span>
															<span className="dup-week-item-meta">{s.nbExos} exo{s.nbExos > 1 ? "s" : ""}</span>
														</div>
														<div className="dup-week-item-dates">
															<span className="dup-week-item-src">
																{d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}
															</span>
														</div>
													</label>
												);
											})}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				{error && <div className="mens-modal-error" style={{ marginTop: 8 }}>{error}</div>}

				<div className="mens-modal-actions dup-week-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={submitting}>Annuler</button>
					<button
						className="mens-btn primary"
						onClick={handleConfirm}
						disabled={loading || submitting || !selectedSession}
						title={!selectedSession ? "Sélectionne une séance à dupliquer" : ""}
					>
						{submitting
							? "En cours…"
							: !selectedSession
								? "Sélectionne une séance"
								: `Dupliquer vers le ${targetDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}`
						}
					</button>
				</div>
			</div>
		</div>
	);
}
